import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VersionComparison = ({ document, versions, onVersionSelect, onClose }) => {
  const [selectedVersions, setSelectedVersions] = useState([
    versions?.[0]?.id || null,
    versions?.[1]?.id || null
  ]);
  const [comparisonMode, setComparisonMode] = useState('side-by-side');

  const handleVersionSelect = (index, versionId) => {
    const newVersions = [...selectedVersions];
    newVersions[index] = versionId;
    setSelectedVersions(newVersions);
  };

  const getVersionStatus = (version) => {
    if (version?.isCurrent) return { color: 'text-success', label: 'Current' };
    if (version?.isDraft) return { color: 'text-warning', label: 'Draft' };
    return { color: 'text-muted-foreground', label: 'Archived' };
  };

  const mockChanges = [
    {
      id: 1,
      type: 'addition',
      section: 'Section 3.2 - Payment Terms',
      content: `Added clause: "Payment shall be made within 30 days of invoice receipt, with a 2% early payment discount available for payments made within 10 days."`
    },
    {
      id: 2,
      type: 'modification',
      section: 'Section 5.1 - Termination',
      content: `Modified termination notice period from 30 days to 60 days for both parties.`
    },
    {
      id: 3,
      type: 'deletion',
      section: 'Section 7.3 - Liability Cap',
      content: `Removed the liability cap limitation clause as per client request.`
    }
  ];

  const getChangeIcon = (type) => {
    switch (type) {
      case 'addition': return 'Plus';
      case 'modification': return 'Edit3';
      case 'deletion': return 'Minus';
      default: return 'FileText';
    }
  };

  const getChangeColor = (type) => {
    switch (type) {
      case 'addition': return 'text-success';
      case 'modification': return 'text-warning';
      case 'deletion': return 'text-error';
      default: return 'text-foreground';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      {/* Modal */}
      <div className="relative w-full max-w-6xl max-h-[90vh] mx-4 bg-card rounded-lg shadow-legal-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Version Comparison</h2>
            <p className="text-sm text-muted-foreground mt-1">{document.name}</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant={comparisonMode === 'side-by-side' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setComparisonMode('side-by-side')}
            >
              <Icon name="Columns" size={16} />
            </Button>
            <Button
              variant={comparisonMode === 'overlay' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setComparisonMode('overlay')}
            >
              <Icon name="Layers" size={16} />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>

        {/* Version Selectors */}
        <div className="p-6 border-b border-border bg-muted/30">
          <div className="grid grid-cols-2 gap-6">
            {[0, 1]?.map((index) => (
              <div key={index} className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Version {index + 1}
                </label>
                <select
                  value={selectedVersions?.[index] || ''}
                  onChange={(e) => handleVersionSelect(index, e?.target?.value)}
                  className="w-full p-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="">Select version...</option>
                  {versions?.map((version) => {
                    const status = getVersionStatus(version);
                    return (
                      <option key={version?.id} value={version?.id}>v{version?.number}- {version?.date}({status.label})
                                              </option>
                    );
                  })}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Content */}
        <div className="flex-1 overflow-hidden">
          {comparisonMode === 'side-by-side' ? (
            <div className="grid grid-cols-2 h-96">
              {selectedVersions?.map((versionId, index) => {
                const version = versions?.find(v => v?.id === versionId);
                return (
                  <div key={index} className="border-r border-border last:border-r-0 overflow-y-auto">
                    <div className="p-4 border-b border-border bg-muted/30">
                      <h3 className="font-medium text-foreground">
                        {version ? `Version ${version?.number}` : 'Select a version'}
                      </h3>
                      {version && (
                        <p className="text-sm text-muted-foreground">
                          {version?.date} • {version?.author}
                        </p>
                      )}
                    </div>
                    <div className="p-4">
                      {version ? (
                        <div className="space-y-4">
                          <div className="aspect-[3/4] bg-muted rounded-lg flex items-center justify-center">
                            <div className="text-center">
                              <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-2" />
                              <p className="text-sm text-muted-foreground">PDF Preview</p>
                              <p className="text-xs text-muted-foreground">v{version?.number}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Icon name="FileQuestion" size={48} className="mx-auto text-muted-foreground mb-2" />
                          <p className="text-muted-foreground">No version selected</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-96 overflow-y-auto p-4">
              <div className="aspect-[3/4] bg-muted rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Icon name="Layers" size={48} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Overlay Comparison View</p>
                  <p className="text-xs text-muted-foreground">Changes highlighted in context</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Changes Summary */}
        <div className="border-t border-border bg-muted/30">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Changes Summary</h3>
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {mockChanges?.map((change) => (
                <div key={change?.id} className="flex items-start space-x-3 p-3 bg-background rounded-lg">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    change?.type === 'addition' ? 'bg-success/10' :
                    change?.type === 'modification' ? 'bg-warning/10' : 'bg-error/10'
                  }`}>
                    <Icon 
                      name={getChangeIcon(change?.type)} 
                      size={12} 
                      className={getChangeColor(change?.type)} 
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{change?.section}</p>
                    <p className="text-sm text-muted-foreground mt-1">{change?.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-success rounded-full"></div>
              <span>Additions</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-warning rounded-full"></div>
              <span>Modifications</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-error rounded-full"></div>
              <span>Deletions</span>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button variant="default" iconName="Download" iconPosition="left">
              Export Comparison
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionComparison;